import React, { useEffect, useState, useContext } from 'react'
import { Row, Col, Container } from 'react-bootstrap'
import { VendorUser, vendorUserConverter } from '../../../models/VendorUser'
import { firebaseDb } from '../../../services/firebase'
import { Collection, Role } from '../../../config/enums'
import { VendorContext } from '../../../contexts/vendorContext'
import UserAvatar from './userAvatar'
import { roleConverter } from '../../../models/Role'
import { AuthContext } from '../../../contexts/authContext'
import ConfirmDelete from '../../common/modals/confirm'
import { deleteUser } from '../../../services/vendorService'
import { friendlyMessageFor } from '../../../utility/errorHelper'
import { toast } from 'react-toastify'
import constants from '../../../config/constants'

interface VendorUsersProps {
  showWriteActions: boolean
  onAddMemberClick: () => void
}

const VendorUsers: React.FC<VendorUsersProps> = ({
  showWriteActions,
  onAddMemberClick
}) => {
  const authContext = useContext(AuthContext)
  const vendorContext = useContext(VendorContext)
  const [admins, setAdmins] = useState<
    Array<firebase.firestore.DocumentSnapshot<VendorUser>>
  >([])
  const [members, setMembers] = useState<
    Array<firebase.firestore.DocumentSnapshot<VendorUser>>
  >([])

  const [showAddButton, setShowAddButton] = useState(true)
  const [showEditButton, setShowEditButton] = useState(false)
  const [isEditingUsers, setIsEditingUsers] = useState(false)
  const [editButtonText, setEditButtonText] = useState('Remove')
  const [showConfirmDelete, setShowConfirmDelete] = useState(false)
  const [toDeleteUserId, setToDeleteUserId] = useState<string | null>(null)
  const [isDeletingUser, setIsDeletingUser] = useState(false)

  const vendorId = vendorContext?.profile?.vendorId

  useEffect(() => {
    if (!vendorId) {
      return
    }

    let unsubscribe: () => void | null

    firebaseDb
      .collection(Collection.roles)
      .withConverter(roleConverter)
      .get()
      .then(rolesSnap => {
        let adminRoleId = ''
        let memberRoleId = ''

        rolesSnap.forEach(doc => {
          if (doc.data().name === Role.admin) {
            adminRoleId = doc.id
          } else if (doc.data().name === Role.member) {
            memberRoleId = doc.id
          }
        })

        // Listen for changes on vendorUsers
        unsubscribe = firebaseDb
          .collection(Collection.vendorUsers)
          .where('vendorId', '==', vendorId)
          .withConverter(vendorUserConverter)
          .onSnapshot(snap => {
            let adminUsers: Array<firebase.firestore.DocumentSnapshot<
              VendorUser
            >> = []
            let memberUsers: Array<firebase.firestore.DocumentSnapshot<
              VendorUser
            >> = []

            snap.forEach(doc => {
              if (doc.data().roleId === adminRoleId) {
                adminUsers.push(doc)
              } else if (doc.data().roleId === memberRoleId) {
                memberUsers.push(doc)
              }
            })

            setAdmins(adminUsers)
            setMembers(memberUsers)

            if (memberUsers.length > 0 || adminUsers.length > 1) {
              setShowEditButton(true)
            } else {
              setShowEditButton(false)
              setShowAddButton(true)
            }
          })
      })

    return () => {
      // stop listening to vendorUsers changes on component unmount
      if (unsubscribe) {
        unsubscribe()
      }
    }
  }, [vendorId])

  const editButtonClick = () => {
    if (isEditingUsers) {
      setIsEditingUsers(false)
      setEditButtonText('Remove')
      setShowAddButton(true)
    } else {
      setIsEditingUsers(true)
      setEditButtonText('Done')
      setShowAddButton(false)
    }
  }

  const confirmDelete = (userId: string) => {
    setToDeleteUserId(userId)
    setShowConfirmDelete(true)
  }

  const deleteConfirmed = async () => {
    if (toDeleteUserId === null) {
      deleteCanceled()
      return
    }

    try {
      setIsDeletingUser(true)
      await deleteUser(toDeleteUserId)
      deleteCanceled()
      toast.success(constants.alertMessage.userDeleted)
      setIsDeletingUser(false)
    } catch (err) {
      console.error('Delete vendorUser error:', err)
      const message = friendlyMessageFor(err)
      toast.error(message, { autoClose: false })
      setIsDeletingUser(false)
    }
  }

  const deleteCanceled = () => {
    if (isDeletingUser) {
      return
    }

    setToDeleteUserId(null)
    setShowConfirmDelete(false)
  }

  return (
    <Container className="vendor-users">
      <ConfirmDelete
        show={showConfirmDelete}
        title="Delete User"
        body="Are you sure you want to delete this user?"
        confirmText="Delete"
        onConfirmClick={() => {
          deleteConfirmed()
        }}
        onCancelClick={() => {
          deleteCanceled()
        }}
        isLoading={isDeletingUser}
        loadingText="Deleting..."
      />
      <Row>
        <Col lg="4" className="mb-4" style={{ borderRight: '2px dashed #ddd' }}>
          <Row>
            <Col className="text-center mb-2">
              <h6>Admin</h6>
            </Col>
          </Row>
          <Row className="justify-content-center">
            {admins.map((user, index) => {
              return (
                <Col key={index} xs="auto">
                  <UserAvatar
                    userId={user.id}
                    avatarUrl={user.data()!.avatarUrl}
                    name={user.data()!.firstName}
                    showDeleteButton={
                      isEditingUsers && user.id !== authContext?.user?.uid
                    }
                    onDeleteClick={userId => {
                      confirmDelete(userId)
                    }}
                  />
                </Col>
              )
            })}
          </Row>
        </Col>
        <Col lg="8" className="mb-4">
          <Row>
            <Col className="text-center mb-2">
              <h6>Members</h6>
            </Col>
          </Row>
          <Row className="justify-content-center">
            {members.length === 0 ? (
              <Col className="text-center">You don't have any members yet</Col>
            ) : (
              members.map((user, index) => {
                return (
                  <Col key={index} xs="auto">
                    <UserAvatar
                      userId={user.id}
                      avatarUrl={user.data()!.avatarUrl}
                      name={user.data()!.firstName}
                      showDeleteButton={isEditingUsers}
                      onDeleteClick={userId => {
                        confirmDelete(userId)
                      }}
                    />
                  </Col>
                )
              })
            )}
          </Row>
        </Col>
        <Col xs="12" className="text-center">
          {showWriteActions && (
            <Row>
              <Col>
                {showEditButton && (
                  <button
                    data-testid="edit-members"
                    className="mochi-button orange mr-3"
                    onClick={() => {
                      editButtonClick()
                    }}
                  >
                    {editButtonText}
                  </button>
                )}
                {showAddButton && (
                  <button
                    data-testid="add-members"
                    className="mochi-button orange"
                    onClick={() => {
                      onAddMemberClick()
                    }}
                  >
                    Add a member
                  </button>
                )}
              </Col>
            </Row>
          )}
        </Col>
      </Row>
    </Container>
  )
}

export default VendorUsers
